import { useEffect } from 'react';
import { usePortalSiteStore } from '../stores/portalSiteStore';

/**
 * Native splash → first-paint handoff (PD-14693).
 *
 * The Capacitor shell launches with `SplashScreen.launchAutoHide: false`
 * (apps/ecommerce-mobile/capacitor.config.ts), so the native splash stays
 * up while the WebView boots instead of dropping to a blank frame. This
 * controller hides it at the first moment the app has something branded
 * to show: when the portal-site bootstrap resolves (success OR the
 * not-configured 404 → NotFoundPage). index.html already paints the
 * brand-primary background, so the frame behind the fade-out is the
 * branded shell, never white.
 *
 * A failsafe timer hides the splash after a few seconds even if the
 * bootstrap hangs (e.g. dead network) — a stuck splash is strictly worse
 * than the app's own offline/error surfaces.
 *
 * No-op on web: the effect short-circuits unless Capacitor reports a
 * native platform, and the plugin is imported dynamically so the browser
 * bundle never loads it. Renders nothing; mounted once in <App> as a
 * sibling of TenantGate (it must run even when the gate renders 404).
 */

const SPLASH_FAILSAFE_MS = 8000;

function isNativePlatform(): boolean {
	return (
		typeof window !== 'undefined' &&
		(
			window as { Capacitor?: { isNativePlatform?: () => boolean } }
		).Capacitor?.isNativePlatform?.() === true
	);
}

export function NativeSplashController(): null {
	// True once the bootstrap has settled (setSite or setNotConfigured).
	// A hard fetch failure never sets it — the failsafe timer covers that.
	const isResolved = usePortalSiteStore((s) => s.isResolved);

	useEffect(() => {
		if (!isNativePlatform()) {
			return undefined;
		}

		const hide = (): void => {
			void import('@capacitor/splash-screen')
				.then((mod) =>
					// A short fade reads as an intentional handoff; hide() is
					// idempotent so the failsafe firing after a normal hide is
					// harmless.
					mod.SplashScreen.hide({ fadeOutDuration: 200 }),
				)
				.catch(() => {
					// Plugin unavailable (older shell binary) — nothing to hide.
				});
		};

		if (isResolved) {
			hide();
			return undefined;
		}

		const failsafe = window.setTimeout(hide, SPLASH_FAILSAFE_MS);
		return (): void => {
			window.clearTimeout(failsafe);
		};
	}, [isResolved]);

	return null;
}
